// @flow

import React, { useContext } from 'react';
import { useFormikContext } from 'formik';
import { EstimateDetailsContainer } from '@components/estimate-top-level/EstimateDetailsContainer';
import { ImportDiscountsButton } from '@components/custom-discounts/ImportDiscountsButton';
import { PushToSFDCButton } from '@components/custom-discounts/PushToSFDCButton';
import { PREVIOUS_CONTRACT_DETAILS_ENUMS } from '@components/renewal-automation/constants';
import { If } from '@presentational/If';

import {
  COMMITTED_CAPACITY_ENABLED_BACKEND_NAME,
  CREDITS_AND_BURSTS_ENABLED_BACKEND_NAME,
  ESTIMATE_LEVEL_TCO_INPUTS_SECTION_HEADER,
  ESTIMATE_SUMMARY,
  IS_ADVANCED_SECURITY_LIVE_IN_CE1,
  IS_DSP_STARTER_PACK_LIVE_IN_CE1,
  IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1,
  IS_NATIVE_INFERENCE_LIVE_IN_CE1,
  IS_RENEWAL_AUTOMATION_LIVE,
  STREAM_GOVERNANCE_SUMMARY,
} from '../../constants';
import {
  getAccordianPanelDetailsObject,
  getIfAdvancedSecurityIsEnabled,
  getIfCustomDiscountsEnabled,
  getIfCustomerViewIsEnabled,
  getIfUserIsAdminBasedOnGivenDetails,
} from '../../common-utils/utils';
import { AccordionsList } from '../presentational/accordions/AccordionsList';
import { CustomDiscountsTopLevelContainer } from '../custom-discounts/CustomDiscountsTopLevelContainer';
import { CreditsAndBurstsContainer } from '../credits-and-bursts/CreditsAndBurstsContainer';
import { AdvancedSecurityCustomDiscountsContainer } from '../advanced-security/AdvancedSecurityCustomDiscountsContainer';
import { ClustersSummaryContainer } from '../views/clusters-summary/ClustersSummaryContainer';
import { StreamGovernanceSummaryContainer } from '../views/stream-governance-summary/StreamGovernanceSummaryContainer';
import { EstimateSummaryTopLevelContainer } from '../views/estimate-summary/EstimateSummaryTopLevelContainer';
import { UserContext } from '../../contexts/UserContext';
import { EstimateContext } from '../../contexts/EstimateContext';
import { FlinkSummaryContainer } from '../views/flink-pools-summary/FlinkSummaryContainer.jsx';
import { NativeInferenceSummaryContainer } from '../views/native-inference-summary/NativeInferenceSummaryContainer.jsx';
import { EstimateTCOInputsTopLevelContainer } from '../tco/estimate-inputs/EstimateTCOInputsTopLevelContainer';
import { Spacer } from '../presentational/spacing/Spacer';
import { StyledLabel } from '../../common-utils/styledComponents';
import { isAnythingEditedOnEstimatePage } from '../views/utils';
import { useIsSGCNBCApplicable } from '../../custom-hooks/useIsSGCNBCApplicable';
import { useIsEstimateRenewal } from '../../custom-hooks/useIsEstimateRenewal';
import { useIsFedRAMPEstimate } from '../../custom-hooks/useIsFedRAMPEstimate';
import { filterAccordionPanelsByFedRAMPMode } from '../fedramp/utils';
import { CustomerLastContractDetailsContainer } from '../estimate-commit-details/CustomerLastContractDetailsContainer';
import { EstimateCommittedCapacityConfigurationContainer } from '../committed-capacity/EstimateCommittedCapacityConfigurationContainer';
import { EstimateCommittedCapacityMetadataContextProvider } from '../../contexts/EstimateCommittedCapacityMetadataContextProvider';

import { ESTIMATE_ACCORDION_KEYS } from './constants';

const EstimateAccordionsContainer = () => {
  const { values: formikValues, initialValues } = useFormikContext();
  const user = useContext(UserContext);
  const estimateData = useContext(EstimateContext);
  const isUserAdmin = getIfUserIsAdminBasedOnGivenDetails(user);
  const isSGCNBCApplicable = useIsSGCNBCApplicable();
  const isRenewal = useIsEstimateRenewal();
  const isFedRAMPEstimate = useIsFedRAMPEstimate();

  // For CNBC: Admins can always edit, Non-admins can edit only for Net New Customers when ( SG CNBC is applicable (Feature Flag + Net New Customer is true))
  const isCreditsAndBurstsReadOnly = isUserAdmin ? false : !isSGCNBCApplicable;
  const isAnythingEditedOnEstimatePageVar = isAnythingEditedOnEstimatePage(
    formikValues,
    initialValues
  );
  const isCustomerViewEnabled = getIfCustomerViewIsEnabled(estimateData?.inputs);

  const accordionPanels = [
    getAccordianPanelDetailsObject(
      <>
        <StyledLabel basic={true} content="Estimate Details" />
        <If test={!isCustomerViewEnabled}>
          <Spacer x={10} />
          <PushToSFDCButton disabledFromParent={isAnythingEditedOnEstimatePageVar} />
        </If>
      </>,
      <EstimateDetailsContainer />,
      ESTIMATE_ACCORDION_KEYS.ESTIMATE_DETAILS
    ),
  ];

  // Show Customer's Last Contract Details only for renewals and when feature flag is enabled
  if (isRenewal && IS_RENEWAL_AUTOMATION_LIVE) {
    accordionPanels.push(
      getAccordianPanelDetailsObject(
        PREVIOUS_CONTRACT_DETAILS_ENUMS.SECTION_TITLE,
        <CustomerLastContractDetailsContainer />,
        ESTIMATE_ACCORDION_KEYS.PREVIOUS_CONTRACT_DETAILS
      )
    );
  }

  // Show Custom Discounts Panel only if it is enabled and when the Customer View is False
  const isCustomDiscountsEnabled = getIfCustomDiscountsEnabled(estimateData?.inputs);

  if (isCustomDiscountsEnabled && !isCustomerViewEnabled) {
    accordionPanels.push(
      getAccordianPanelDetailsObject(
        <>
          <StyledLabel basic={true} content="Custom Discounts" />
          <If test={IS_RENEWAL_AUTOMATION_LIVE}>
            <Spacer x={10} />
            <ImportDiscountsButton disabledFromParent={isAnythingEditedOnEstimatePageVar} />
          </If>
        </>,
        // RO mode false for now but in future we can control if we want entire section as RO or not using this flag at global level
        <CustomDiscountsTopLevelContainer readOnlyMode={false} />,
        ESTIMATE_ACCORDION_KEYS.CUSTOM_DISCOUNTS
      )
    );
  }

  // Show Committed Capacity Panel only if it is enabled
  if (IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1 && formikValues?.[COMMITTED_CAPACITY_ENABLED_BACKEND_NAME]) {
    accordionPanels.push(
      getAccordianPanelDetailsObject(
        IS_DSP_STARTER_PACK_LIVE_IN_CE1 ? 'Advantage Packages' : 'Committed Capacity',
        <EstimateCommittedCapacityMetadataContextProvider>
          <EstimateCommittedCapacityConfigurationContainer />
        </EstimateCommittedCapacityMetadataContextProvider>,
        ESTIMATE_ACCORDION_KEYS.COMMITTED_CAPACITY
      )
    );
  }

  // Show Credits And Bursts Panel only if it is enabled
  if (formikValues?.[CREDITS_AND_BURSTS_ENABLED_BACKEND_NAME]) {
    accordionPanels.push(
      getAccordianPanelDetailsObject(
        'Credits And Bursts',
        <CreditsAndBurstsContainer readOnlyMode={isCreditsAndBurstsReadOnly} />,
        ESTIMATE_ACCORDION_KEYS.CREDITS_AND_BURSTS
      )
    );
  }

  // Show Advanced Security Custom Discounts Panel only when Custom Discounts and Advanced Security are both enabled and not in Customer View
  const isAdvancedSecurityEnabled = getIfAdvancedSecurityIsEnabled(estimateData?.inputs);
  if (
    IS_ADVANCED_SECURITY_LIVE_IN_CE1 &&
    !isCustomerViewEnabled &&
    isCustomDiscountsEnabled &&
    isAdvancedSecurityEnabled
  ) {
    accordionPanels.push(
      getAccordianPanelDetailsObject(
        'Advanced Security Custom Discounts',
        <AdvancedSecurityCustomDiscountsContainer />,
        ESTIMATE_ACCORDION_KEYS.ADVANCED_SECURITY_CUSTOM_DISCOUNTS
      )
    );
  }

  // Clusters Summary
  accordionPanels.push(
    getAccordianPanelDetailsObject(
      'Clusters Summary',
      <ClustersSummaryContainer />,
      ESTIMATE_ACCORDION_KEYS.CLUSTERS_SUMMARY
    )
  );

  // Flink Summary
  accordionPanels.push(
    getAccordianPanelDetailsObject(
      'Flink Pools ( FP ) Summary',
      <FlinkSummaryContainer />,
      ESTIMATE_ACCORDION_KEYS.FLINK_SUMMARY
    )
  );

  // Native Inference Summary
  if (IS_NATIVE_INFERENCE_LIVE_IN_CE1) {
    accordionPanels.push(
      getAccordianPanelDetailsObject(
        'Native Inference Summary',
        <NativeInferenceSummaryContainer />,
        ESTIMATE_ACCORDION_KEYS.NATIVE_INFERENCE_SUMMARY
      )
    );
  }

  // StreamGovernance Summary
  accordionPanels.push(
    getAccordianPanelDetailsObject(
      STREAM_GOVERNANCE_SUMMARY,
      <StreamGovernanceSummaryContainer />,
      ESTIMATE_ACCORDION_KEYS.STREAM_GOVERNANCE_SUMMARY
    )
  );

  if (!isCustomerViewEnabled) {
    accordionPanels.push(
      getAccordianPanelDetailsObject(
        ESTIMATE_LEVEL_TCO_INPUTS_SECTION_HEADER,
        <EstimateTCOInputsTopLevelContainer />,
        ESTIMATE_ACCORDION_KEYS.TCO_INPUTS
      )
    );
  }

  // Estimate Summary
  accordionPanels.push(
    getAccordianPanelDetailsObject(
      ESTIMATE_SUMMARY,
      <EstimateSummaryTopLevelContainer />,
      ESTIMATE_ACCORDION_KEYS.ESTIMATE_SUMMARY
    )
  );

  const filteredAccordionPanelsByFedRAMPMode = filterAccordionPanelsByFedRAMPMode(
    accordionPanels,
    isFedRAMPEstimate
  );

  return <AccordionsList panels={filteredAccordionPanelsByFedRAMPMode} />;
};

export default EstimateAccordionsContainer;
